import { motion } from "framer-motion";
import { LumenLogo } from "../LumenLogo";
import { Sparkles, ArrowRight, Clock } from "lucide-react";

export const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center justify-center hero-gradient overflow-hidden"
      data-testid="hero-section"
    >
      {/* Decorative sacred geometry */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 right-10 w-64 h-64 opacity-[0.03]"
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        >
          <svg viewBox="0 0 100 100" fill="none" stroke="#047857" strokeWidth="0.5">
            <circle cx="50" cy="50" r="45" />
            <circle cx="50" cy="50" r="35" />
            <circle cx="50" cy="50" r="25" />
            <polygon points="50,5 95,75 5,75" />
            <polygon points="50,95 5,25 95,25" />
          </svg>
        </motion.div>
        
        <motion.div
          className="absolute bottom-20 left-10 w-48 h-48 opacity-[0.03]"
          animate={{ rotate: -360 }}
          transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        >
          <svg viewBox="0 0 100 100" fill="none" stroke="#D97706" strokeWidth="0.5">
            <polygon points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5" />
            <polygon points="50,20 80,35 80,65 50,80 20,65 20,35" />
          </svg>
        </motion.div>
      </div>

      <div className="lumen-container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo */}
          <motion.div
            className="flex justify-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <LumenLogo size="lg" />
          </motion.div>

          {/* Main headline */}
          <motion.h1
            className="hero-title font-cormorant text-text-main mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            L'IA qui n'imite pas ta lumière :{" "}
            <span className="italic text-emerald-700">elle l'amplifie.</span>
          </motion.h1>

          {/* Subheadline - Problem hook */}
          <motion.p
            className="text-base md:text-lg text-text-muted max-w-2xl mx-auto mb-8 font-manrope leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Tu es praticien(ne), thérapeute, accompagnant(e). Tu fais un travail <strong>profond</strong>.
            <br />Mais côté visibilité ? Tu jongles entre com', copy, posts, stories… <strong>seul(e)</strong>.
          </motion.p>

          {/* Value proposition */}
          <motion.div
            className="glass-card p-6 md:p-8 max-w-2xl mx-auto mb-10"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <p className="font-manrope text-text-main leading-relaxed">
              <strong className="text-emerald-700">LUMEN</strong>, c'est une <strong>team d'assistants IA</strong> (paiement unique) 
              qui verrouille ton ADN, respecte tes valeurs, et te rend <strong className="text-emerald-700">≈ 15h/semaine</strong>.
              <br /><span className="text-text-muted italic">Tu récupères du temps pour ton vrai métier : accompagner.</span>
            </p>
          </motion.div>

          {/* Time saved highlight */}
          <motion.div
            className="flex justify-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div className="inline-flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-full px-6 py-3">
              <Clock className="w-5 h-5 text-emerald-600" />
              <span className="font-manrope font-medium text-emerald-700">
                Paiement unique • Accès à vie • ≈15h économisées/semaine
              </span>
            </div>
          </motion.div>

          {/* Price */}
          <motion.div
            className="flex flex-col items-center gap-2 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <span className="text-4xl md:text-5xl font-cormorant font-semibold text-emerald-700">333€</span>
            <span className="text-sm text-text-muted font-manrope">Accès immédiat • Copiers-collers prêts</span>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
          >
            <a 
              href="https://gillesdelieuze.com/store-checkout-03/"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-primary group"
              data-testid="hero-cta-button"
            >
              <Sparkles className="w-5 h-5" />
              Je veux LUMEN
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-text-muted font-manrope"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Actionnable en 7 jours
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Compatible ChatGPT, Claude, Gemini
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Fonctionne même en gratuit
            </span>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="w-5 h-8 border-2 border-emerald-400/60 rounded-full flex justify-center pt-1.5 bg-white/30 backdrop-blur-sm">
          <div className="w-1 h-2 bg-emerald-400 rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};
